import React, { FC, Children, CSSProperties, useEffect, useRef } from 'react';
import { Navigation, Pagination, Thumbs, FreeMode } from 'swiper/modules'; // eslint-disable-line import/no-unresolved
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react'; // eslint-disable-line import/no-unresolved
import 'swiper/css'; // eslint-disable-line import/no-unresolved
import 'swiper/css/navigation'; // eslint-disable-line import/no-unresolved
import 'swiper/css/pagination'; // eslint-disable-line import/no-unresolved
import 'swiper/css/scrollbar'; // eslint-disable-line import/no-unresolved
import 'swiper/css/free-mode'; // eslint-disable-line import/no-unresolved
import classnames from 'classnames';
import useClassNames from 'helpers/hooks/useClassNames';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { useSliderModal } from './hooks/useSliderModal';
import type { SlideImageData } from './hooks/useSliderModal';
import { useSwiperSetup } from './hooks/useSwiperSetup';
import SliderModal from './slider-modal';
import SliderNavigation, { SliderNavigationProps } from './slider-navigation';

// Re-export for consumers that need to type slideImageData prop
export type { SlideImageData };

export type SliderProps = SliderNavigationProps & {
  allowArrowsOnTouchDevice?: boolean;
  className?: string;
  containerClassName?: string;
  spaceBetween?: number;
  slidesPerView?: number | 'auto';
  dots?: boolean;
  fitToSlides?: boolean;
  slideWidth?: number;
  withThumbs?: boolean;
  slideWidthIsFlexible?: boolean;
  freeMode?: boolean;
  watchSlidesProgress?: boolean;
  enableModal?: boolean;
  slideImageData?: SlideImageData[];
} & Omit<SwiperProps, 'slidesPerView'>;

const Slider: FC<SliderProps> = ({
  className = '',
  containerClassName = '',
  slideWidth = 300,
  slidesPerView,
  fitToSlides = false,
  arrows = false,
  innerArrows = false,
  solidArrows = false,
  dots = true,
  spaceBetween = 20,
  withThumbs = false,
  freeMode = false,
  watchSlidesProgress = false,
  enableModal = false,
  slideImageData,
  children,
  onSwiper,
  onInit,
  allowTouchMove,
  arrowButtonClasses = '',
  prevButtonStyles = {},
  nextButtonStyles = {},
  prevArrowStyles = {},
  nextArrowStyles = {},
  compactNavigation,
  arrowIconNavigation,
  slideWidthIsFlexible,
  allowArrowsOnTouchDevice = true,
  ...props
}) => {
  const { isTouchDevice, hasTouchCapability } = useTouchDevice();

  const { init, swiperRef, navigationPrevRef, navigationNextRef, thumbsSwiper, handleInit, handleOnSwiper } =
    useSwiperSetup({ onInit, onSwiper, withThumbs });

  // Enable Swiper touch if device HAS touch capability (not just currently using touch).
  // This fixes the "first touch doesn't work" issue on hybrid devices (Surface, iPad with keyboard)
  // where the user starts with mouse but then touches the screen.
  // We use hasTouchCapability (static) rather than isTouchDevice (dynamic) so Swiper's
  // internal handler is ready for touch input before the first touch event arrives.
  const effectiveAllowTouchMove = allowTouchMove !== false && hasTouchCapability;
  useEffect(() => {
    if (swiperRef.current && !swiperRef.current.destroyed) {
      swiperRef.current.allowTouchMove = effectiveAllowTouchMove;
    }
  }, [effectiveAllowTouchMove, swiperRef]);

  // Modal functionality - hook always called per React rules, but only active when enabled
  const {
    modalOpen,
    activeImageUrl,
    activeImageMedia,
    activeImageAlt,
    images: modalImages,
    activeIndex: modalActiveIndex,
    totalImages: modalTotalImages,
    canGoNext,
    canGoPrevious,
    handleSlideClick,
    handleCloseModal,
    goToImage,
    goToNext,
    goToPrevious,
  } = useSliderModal(enableModal);

  // Warn once if slideImageData doesn't match children count
  const hasWarnedMismatch = useRef(false);
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && enableModal && slideImageData && !hasWarnedMismatch.current) {
      const childCount = Children.count(children);
      if (slideImageData.length !== childCount) {
        console.warn(
          `Slider: slideImageData length (${slideImageData.length}) doesn't match children count (${childCount}). ` +
          'This may cause incorrect images to display in the modal.',
        );
        hasWarnedMismatch.current = true;
      }
    }
  }, [enableModal, slideImageData, children]);

  const validToFit = Boolean(fitToSlides && slideWidth && typeof slidesPerView === 'number');
  const sliderWidth: CSSProperties['width'] = validToFit
    ? `${spaceBetween * ((slidesPerView as number) - 1) + slideWidth * (slidesPerView as number)}px`
    : '100%';

  const containerClassNames = useClassNames([
    containerClassName,
    'slider_container relative',
    !init ? 'invisible' : 'visible',
    {
      'slider__container--fit': validToFit,
      'slider__container--with-thumbs': withThumbs,
    },
  ]);

  const slidesClassName = useClassNames(['slider', className]);

  const slideProps: React.CSSProperties = {
    width: slideWidthIsFlexible ? 'fit-content' : typeof slidesPerView === 'number' ? '100%' : `${slideWidth}px`,
    height: '100%',
    flexShrink: 0,
    maxWidth: typeof slidesPerView === 'number' ? '100%' : `${slideWidth}px`,
  };

  const slides = Children.map(children, (child, index) => {
    const imageData = slideImageData?.[index];
    // Only enable modal interaction if image has a valid source (src URL or Cloudinary mediaId)
    const hasValidSource = Boolean(imageData?.src || imageData?.media?.mediaId);
    const hasImageData = enableModal && imageData && hasValidSource;

    return (
      <SwiperSlide style={slideProps}>
        <div
          onClick={hasImageData ? (e) => handleSlideClick(imageData, e.currentTarget, slideImageData, index) : undefined}
          onKeyDown={
            hasImageData
              ? (e) => {
                // Only keys aimed at the slide itself: a control rendered inside it (e.g. the
                // gallery's finish chips) must keep its own Enter/Space activation.
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSlideClick(imageData, e.currentTarget as HTMLElement, slideImageData, index);
                }
              }
              : undefined
          }
          role={hasImageData ? 'button' : undefined}
          tabIndex={hasImageData ? 0 : undefined}
          aria-label={hasImageData ? 'View enlarged image' : undefined}
          className={classnames('h-full', hasImageData ? 'cursor-pointer' : undefined)}
        >
          {child}
        </div>
      </SwiperSlide>
    );
  });

  const modules = [Navigation, Pagination, Thumbs];
  if (freeMode) modules.push(FreeMode);

  return (
    <div className={containerClassNames}>
      <Swiper
        className={slidesClassName}
        modules={modules}
        thumbs={{ swiper: thumbsSwiper }}
        pagination={dots ? { clickable: true, bulletActiveClass: 'slider__bullet--active' } : false}
        slidesPerView={slidesPerView ?? 'auto'}
        spaceBetween={spaceBetween}
        freeMode={freeMode}
        watchSlidesProgress={watchSlidesProgress}
        resistanceRatio={0}
        edgeSwipeDetection={true}
        edgeSwipeThreshold={20}
        preventClicks={false}
        preventClicksPropagation={false}
        style={{ width: sliderWidth }}
        navigation={{
          prevEl: navigationPrevRef.current,
          nextEl: navigationNextRef.current,
        }}
        onSwiper={handleOnSwiper}
        observer
        observeParents
        onInit={handleInit}
        allowTouchMove={effectiveAllowTouchMove}
        {...props}
      >
        {slides}
      </Swiper>

      <SliderNavigation
        compactNavigation={compactNavigation}
        arrowIconNavigation={arrowIconNavigation}
        arrows={allowArrowsOnTouchDevice ? arrows : arrows && !isTouchDevice}
        arrowButtonClasses={arrowButtonClasses}
        prevButtonStyles={prevButtonStyles}
        nextButtonStyles={nextButtonStyles}
        prevArrowStyles={prevArrowStyles}
        nextArrowStyles={nextArrowStyles}
        totalSlides={slides?.length}
        swiperRef={swiperRef?.current}
        innerArrows={innerArrows}
        solidArrows={solidArrows}
        isLoopMode={props.loop}
      />

      {enableModal && (
        <SliderModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          imageUrl={activeImageUrl}
          imageAlt={activeImageAlt}
          imageMedia={activeImageMedia}
          images={modalImages}
          activeIndex={modalActiveIndex}
          totalImages={modalTotalImages}
          onNavigate={goToImage}
          onPrevious={goToPrevious}
          onNext={goToNext}
          canGoNext={canGoNext}
          canGoPrevious={canGoPrevious}
        />
      )}
    </div>
  );
};

export default Slider;
